import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import UserDropdown from "../components/UserDropdown";
import {
  FileUploadResponse,
  TaskStatusResponse,
  ProcessedFileInfo,
} from "../types/knowledge";
import "./UploadFilesPage.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";

const ACCEPTED_FILE_TYPES = [
  ".pdf",
  ".xlsx",
  ".xls",
  ".csv",
  ".jpg",
  ".jpeg",
  ".png",
  ".docx",
];

const UploadFilesPage: React.FC = () => {
  const { user, token, tenantId, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<TaskStatusResponse | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(
    null,
  );

  useEffect(() => {
    if (!user?.is_superuser) {
      navigate("/chats");
      return;
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [user, navigate, pollingInterval]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach((file) => {
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      if (ACCEPTED_FILE_TYPES.includes(fileExtension)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setError(
        `Invalid file types: ${invalidFiles.join(
          ", ",
        )}. Accepted types: ${ACCEPTED_FILE_TYPES.join(", ")}`,
      );
    } else {
      setError("");
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach((file) => {
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      if (ACCEPTED_FILE_TYPES.includes(fileExtension)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setError(
        `Invalid file types: ${invalidFiles.join(
          ", ",
        )}. Accepted types: ${ACCEPTED_FILE_TYPES.join(", ")}`,
      );
    } else {
      setError("");
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (!token || selectedFiles.length === 0) return;

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(
        `${API_BASE_URL}/text-extraction/process-files/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
          },
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to upload files");
      }

      const data: FileUploadResponse = await response.json();
      setTaskId(data.task_id);

      // Start polling for task status
      startPolling(data.task_id);
    } catch (err) {
      console.error("Error uploading files:", err);
      setError(err instanceof Error ? err.message : "Failed to upload files");
      setUploading(false);
    }
  };

  const fetchTaskStatus = async (id: string) => {
    if (!token) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/text-extraction/task-status/${id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            ...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch task status");
      }

      const data: TaskStatusResponse = await response.json();
      setTaskStatus(data);

      // Stop polling if task is completed or failed
      if (data.status === "completed" || data.status === "failed") {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        setUploading(false);
      }
    } catch (err) {
      console.error("Error fetching task status:", err);
    }
  };

  const startPolling = (id: string) => {
    // Initial fetch
    fetchTaskStatus(id);

    // Poll every 2 seconds
    const interval = setInterval(() => {
      fetchTaskStatus(id);
    }, 2000);

    setPollingInterval(interval);
  };

  const resetUpload = () => {
    setSelectedFiles([]);
    setTaskId(null);
    setTaskStatus(null);
    setError("");
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "completed":
        return "#28a745";
      case "failed":
        return "#dc3545";
      case "processing":
        return "#ffc107";
      default:
        return "#6c757d";
    }
  };

  if (!user?.is_superuser) {
    return null;
  }

  return (
    <div className="upload-files-container">
      <header className="upload-header">
        <div className="header-content">
          <div className="header-left">
            <button
              onClick={() => navigate("/knowledge-documents")}
              className="back-button"
            >
              ← Back to Documents
            </button>
            <h1>Upload Knowledge Documents</h1>
          </div>
          <UserDropdown
            username={user?.username || "User"}
            isSuperuser={user?.is_superuser}
            onLogout={async () => {
              await logout();
              navigate("/login");
            }}
            onManageKnowledge={() => navigate("/knowledge-documents")}
          />
        </div>
      </header>

      <main className="upload-main">
        {!taskId ? (
          <>
            <div
              className="upload-dropzone"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="dropzone-content">
                <div className="upload-icon">📁</div>
                <h3>Drag & Drop Files Here</h3>
                <p>or click to browse</p>
                <p className="accepted-types">
                  Accepted: {ACCEPTED_FILE_TYPES.join(", ")}
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_FILE_TYPES.join(",")}
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            {selectedFiles.length > 0 && (
              <div className="selected-files-section">
                <h2>Selected Files ({selectedFiles.length})</h2>
                <div className="selected-files-list">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="selected-file-item">
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">
                          {formatFileSize(file.size)}
                        </span>
                      </div>
                      <button
                        className="remove-file-btn"
                        onClick={() => removeFile(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <div className="upload-actions">
                  <button
                    className="upload-button"
                    onClick={uploadFiles}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload Files"}
                  </button>
                  <button
                    className="clear-button"
                    onClick={() => setSelectedFiles([])}
                    disabled={uploading}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="task-status-section">
            <div className="status-header">
              <h2>Processing Status</h2>
              <button className="new-upload-button" onClick={resetUpload}>
                Upload More Files
              </button>
            </div>

            {taskStatus && (
              <>
                <div className="status-overview">
                  <div className="status-card">
                    <h3>Overall Progress</h3>
                    <div className="progress-circle">
                      <div className="progress-value">
                        {taskStatus.progress}
                      </div>
                    </div>
                    <div
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(taskStatus.status),
                      }}
                    >
                      {taskStatus.status.toUpperCase()}
                    </div>
                  </div>

                  <div className="status-card">
                    <h3>Files Summary</h3>
                    <div className="status-stats">
                      <div className="stat-item">
                        <span className="stat-label">Total Files:</span>
                        <span className="stat-value">
                          {taskStatus.total_files}
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Processed:</span>
                        <span className="stat-value">
                          {taskStatus.processed_files}
                        </span>
                      </div>
                      <div className="stat-item success">
                        <span className="stat-label">Successful:</span>
                        <span className="stat-value">
                          {taskStatus.successful_files}
                        </span>
                      </div>
                      <div className="stat-item failed">
                        <span className="stat-label">Failed:</span>
                        <span className="stat-value">
                          {taskStatus.failed_files}
                        </span>
                      </div>
                    </div>
                  </div>

                  {taskStatus.total_processing_time && (
                    <div className="status-card">
                      <h3>Processing Time</h3>
                      <div className="processing-time">
                        {taskStatus.total_processing_time.toFixed(2)} seconds
                      </div>
                    </div>
                  )}
                </div>

                <div className="files-status-list">
                  <h3>Individual File Status</h3>
                  {taskStatus.files.map((file, index) => (
                    <div key={index} className="file-status-item">
                      <div className="file-status-header">
                        <div className="file-status-name">
                          <span className="file-icon">📄</span>
                          <span>{file.filename}</span>
                        </div>
                        <div
                          className="file-status-badge"
                          style={{
                            backgroundColor: getStatusColor(file.status),
                          }}
                        >
                          {file.status}
                        </div>
                      </div>

                      <div className="file-status-details">
                        <div className="detail-item">
                          <span className="detail-label">Size:</span>
                          <span>{formatFileSize(file.file_size)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Type:</span>
                          <span>{file.file_type.toUpperCase()}</span>
                        </div>
                        {file.processing_time && (
                          <div className="detail-item">
                            <span className="detail-label">
                              Processing Time:
                            </span>
                            <span>{file.processing_time.toFixed(2)}s</span>
                          </div>
                        )}
                        {file.chunks_created !== undefined && (
                          <div className="detail-item">
                            <span className="detail-label">Chunks:</span>
                            <span>{file.chunks_created}</span>
                          </div>
                        )}
                        {file.word_count !== undefined && (
                          <div className="detail-item">
                            <span className="detail-label">Words:</span>
                            <span>{file.word_count}</span>
                          </div>
                        )}
                        {file.embedding_status && (
                          <div className="detail-item">
                            <span className="detail-label">Embeddings:</span>
                            <span>
                              {file.embeddings_processed}/{file.total_chunks} (
                              {file.embedding_status})
                            </span>
                          </div>
                        )}
                      </div>

                      {file.error && (
                        <div className="file-error">
                          <strong>Error:</strong> {file.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default UploadFilesPage;
